import React from 'react';
import { motion } from 'framer-motion';
import {
    Users, Search, RefreshCw, Trash2, Shield,
    UserPlus, MoreHorizontal, UserCheck, ShieldAlert,
    User
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../../components/ui/table";
import { Badge } from "../../../components/ui/badge";

const UsersSection = ({
    users,
    loading,
    searchQuery,
    setSearchQuery,
    syncUsers,
    updateUserRole,
    deleteUser,
    isMobile
}) => {
    const filteredUsers = users.filter(u =>
        u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.displayName?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-2">
                <div>
                    <h3 className="text-2xl font-black text-foreground tracking-tight flex items-center gap-2">
                        <Users className="text-primary" /> User Management
                    </h3>
                    <p className="text-muted-foreground text-xs font-bold uppercase tracking-widest mt-1 opacity-70">
                        Manage roles, access, and account security
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={syncUsers}
                    className="rounded-xl border-border/60 hover:bg-primary/10 hover:text-primary font-bold gap-2 self-end md:self-auto"
                >
                    <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Sync Auth Records
                </Button>
            </div>

            <div className="relative group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" size={18} />
                <Input
                    placeholder="Search users by name or email..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="pl-12 py-6 rounded-2xl bg-background/50 backdrop-blur-xl border-border/40 focus:ring-primary/20 focus:border-primary/40 transition-all text-sm font-bold tracking-tight text-foreground"
                />
            </div>

            {loading ? (
                <div className="text-center py-20 bg-card/40 backdrop-blur-xl rounded-[2rem] border border-border/40">
                    <RefreshCw size={32} className="animate-spin text-primary/40 mx-auto mb-4" />
                    <div className="text-muted-foreground font-black uppercase tracking-widest text-xs">Syncing user data...</div>
                </div>
            ) : (
                <div className="bg-card/40 backdrop-blur-xl rounded-[2rem] border border-border/40 overflow-hidden shadow-2xl">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader className="bg-secondary/20">
                                <TableRow className="hover:bg-transparent border-border/40">
                                    <TableHead className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-muted-foreground">User</TableHead>
                                    <TableHead className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-muted-foreground">Email</TableHead>
                                    <TableHead className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-muted-foreground">Role</TableHead>
                                    <TableHead className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-muted-foreground hidden md:table-cell">Access ID</TableHead>
                                    <TableHead className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-muted-foreground text-right border-border/40">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredUsers.map((u, i) => (
                                    <TableRow
                                        key={u.id}
                                        className="border-border/40 hover:bg-primary/5 transition-colors group"
                                    >
                                        <TableCell className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                {u.photoURL ? (
                                                    <img src={u.photoURL} alt="" className="w-10 h-10 rounded-xl object-cover ring-2 ring-primary/10" />
                                                ) : (
                                                    <div className="w-10 h-10 rounded-xl bg-secondary/80 flex items-center justify-center text-foreground font-black text-sm border border-border/60">
                                                        {(u.displayName || u.email || 'U')[0].toUpperCase()}
                                                    </div>
                                                )}
                                                <span className="font-black text-sm text-foreground tracking-tight">{u.displayName || 'Anonymous'}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="px-6 py-4 text-xs font-bold text-muted-foreground opacity-80">{u.email}</TableCell>
                                        <TableCell className="px-6 py-4">
                                            <select
                                                value={u.role || 'user'}
                                                onChange={e => updateUserRole(u.id, e.target.value)}
                                                className={`px-3 py-1.5 rounded-xl border font-black text-[10px] uppercase tracking-widest appearance-none cursor-pointer outline-none transition-all ${u.role === 'admin'
                                                    ? 'bg-rose-500/10 border-rose-500/40 text-rose-500 hover:bg-rose-500/20'
                                                    : u.role === 'company'
                                                        ? 'bg-amber-500/10 border-amber-500/40 text-amber-500 hover:bg-amber-500/20'
                                                        : 'bg-emerald-500/10 border-emerald-500/40 text-emerald-500 hover:bg-emerald-500/20'
                                                    }`}
                                            >
                                                <option value="student">Student</option>
                                                <option value="user">User</option>
                                                <option value="company">Company</option>
                                                <option value="admin">Admin</option>
                                            </select>
                                        </TableCell>
                                        <TableCell className="px-6 py-4 text-[10px] font-mono font-bold text-muted-foreground opacity-60 hidden md:table-cell">
                                            {u.id.substring(0, 12)}...
                                        </TableCell>
                                        <TableCell className="px-6 py-4 text-right">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => deleteUser(u.id)}
                                                className="w-9 h-9 rounded-xl bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white transition-all group-hover:scale-105"
                                            >
                                                <Trash2 size={16} />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    {filteredUsers.length === 0 && (
                        <div className="text-center py-20 opacity-40">
                            <Users size={48} className="mx-auto mb-4" />
                            <div className="font-black uppercase tracking-widest text-xs">No users found</div>
                        </div>
                    )}
                </div>
            )}
        </motion.div>
    );
};

export default UsersSection;
